import classnames from 'classnames/bind';
import PropTypes from 'prop-types';
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';

import { shuffleArray } from '../helpers';
import matchMobile from '../helpers/util/match-mobile';
import { LinearNavigation } from '../index';

import { Card } from './card';
import styles from './flash-cards.module.scss';

const cx = classnames.bind(styles);
const SWAP_FORWARD = 'forward-swap';
const SWAP_BACKWARD = 'backward-swap';
const ANIMATION_DELAY = 700;
const SHUFFLE_DELAY = 300;

const FlashCards = forwardRef(({ data, onComplete, sectionOrder }, ref) => {
  const dataDeepCopy = JSON.parse(JSON.stringify(data));
  const [cards, updateCards] = useState(dataDeepCopy);
  const [current, setCurrent] = useState(0);
  const [currentCard, setCurrentCard] = useState(cards[current]);
  const [expanded, setExpanded] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [cardSwap, setCardSwap] = useState(null);
  const [cardFlip, setCardFlip] = useState(null);
  const [cardsReset, resetCards] = useState(null);

  const totalCount = useMemo(() => dataDeepCopy.length, [dataDeepCopy.length]);
  const containerEl = useRef(null);
  const scrollTop = () => containerEl.current.scrollTo(0, 0);
  const stackEl = useRef(null);

  // take the top card in the stack and move it to the bottom
  const prependCard = () => {
    const card = stackEl.current.lastChild;
    stackEl.current.prepend(card);
  };

  // take the bottom card in the stack and move it to the top
  const appendCard = () => {
    const card = stackEl.current.firstChild;
    stackEl.current.append(card);
  };

  // animation that happens when the next button is clicked
  const nextAnimation = () => {
    setCardSwap(null);
    setCardFlip(null);
    prependCard();
  };

  // animation that happens when the back button is clicked
  const backAnimation = () => {
    setCardSwap(null);
    appendCard();
  };

  // animation that happens when the next button is clicked in mobile
  const nextAnimationMobile = () => {
    setTimeout(() => {
      setCardSwap(null);
      setCardFlip(null);
      prependCard();
    }, 100);
  };

  // animation that happens when the next button is clicked in mobile
  const backAnimationMobile = () => {
    setTimeout(() => {
      setCardSwap(null);
    }, 100);

    appendCard();
  };

  // sets the current card when the back button is clicked
  const backSetCurrentCard = () => {
    setCurrent(current - 1);
    scrollTop();
  };

  // method when the back button is clicked
  const back = () => {
    if (current === 0) {
      return;
    }
    setCardSwap(SWAP_BACKWARD);

    if (isMobile) {
      backSetCurrentCard();
      backAnimationMobile();
    } else {
      backSetCurrentCard();
      setTimeout(() => {
        backAnimation();
      }, ANIMATION_DELAY);
    }
  };

  // method when the next button is clicked
  const next = () => {
    if (current === totalCount - 1 || !currentCard.completed) {
      return;
    }
    setCurrent(current + 1);
    scrollTop();
    setCardSwap(SWAP_FORWARD);
    setCardFlip(true);

    if (isMobile) {
      nextAnimationMobile();
    } else {
      setTimeout(() => {
        nextAnimation();
      }, ANIMATION_DELAY);
    }
  };

  // SHUFFLE LOGIC
  const shuffleDelay = (ms) => {
    return new Promise((resolve) => setTimeout(resolve, ms));
  };

  const shuffleAnimation = (index) => {
    return shuffleDelay(SHUFFLE_DELAY).then(() => {
      setCurrent(index);
      scrollTop();
      setCardSwap(SWAP_FORWARD);
      setCardFlip(true);

      setTimeout(() => {
        nextAnimation();
      }, SHUFFLE_DELAY);
    });
  };

  const shuffle = async () => {
    for (let index = 0; index < getCards().length; index++) {
      await shuffleAnimation(index);
    }

    setCurrent(0);
    setTimeout(() => {
      resetCards(null);
    }, SHUFFLE_DELAY);
  };
  // END SHUFFLE LOGIC

  // get cards and reverse order them so they're logically ordered in the stack
  const getCards = () => [...cards].reverse();

  const onExpand = useCallback(
    (size) => {
      const originalCard = cards.find((c) => c.id === currentCard.id);
      const isExpanded = !!size;

      originalCard.expanded = isExpanded;
      setExpanded(isExpanded ? size + 20 : null);
    },
    [cards, currentCard],
  );

  const onFlip = () => {
    const originalCard = cards.find((c) => c.id === currentCard.id);
    originalCard.completed = true;
    setCurrentCard({ ...originalCard });

    const isComplete = cards.findIndex((c) => !c.completed) === -1;

    if (isComplete) {
      onComplete(true);
    }
  };

  const isCurrentCard = (card) => currentCard.id === card.id;

  const isLastCard = () => {
    return current === getCards().length - 1;
  };

  useEffect(() => {
    setCurrentCard(cards[current]);
  }, [cards, current]);

  useEffect(() => {
    setIsMobile(matchMobile.isMobile);

    const handleMediaChange = (matches) => {
      scrollTop();
      setIsMobile(matches);
    };

    matchMobile.addListener(handleMediaChange);

    return () => matchMobile.removeListener(handleMediaChange);
  }, []);

  // The component instance will be extended
  // with whatever you return from the callback passed
  // as the second argument
  useImperativeHandle(ref, () => ({
    triggerShuffle() {
      updateCards(shuffleArray(dataDeepCopy));
      setCurrent(0);
      setCurrentCard(cards[0]);
      setCardFlip(true);

      if (!isMobile) {
        resetCards(true);
        shuffle();
      }

      setTimeout(() => {
        setCardFlip(null);
      }, 100);
    },
  }));

  return (
    <div className={styles.container} ref={containerEl}>
      <div
        className={cx({ stack: true, [cardSwap]: cardSwap, ['reset']: cardsReset })}
        style={expanded ? { height: expanded } : {}}
        ref={stackEl}
      >
        {getCards().map((card) => (
          <Card
            key={card.id}
            cardFlip={cardFlip}
            data={card}
            onExpand={onExpand}
            onFlip={onFlip}
            isCurrentCard={isCurrentCard(card)}
            onNext={next}
            lastCard={isLastCard()}
            sectionOrder={sectionOrder}
            navigation={
              <LinearNavigation
                item={card}
                allItems={cards}
                onBack={back}
                onNext={next}
                isStrict={true}
              />
            }
          />
        ))}
      </div>
      <div className={styles.nav}>
        <LinearNavigation
          item={currentCard}
          allItems={cards}
          onBack={back}
          onNext={next}
          isStrict={true}
        />
      </div>
    </div>
  );
});

FlashCards.displayName = 'FlashCards';

FlashCards.propTypes = {
  data: PropTypes.array,
  onComplete: PropTypes.func,
  sectionOrder: PropTypes.arrayOf(PropTypes.string),
};

FlashCards.defaultProps = {
  onComplete: () => {},
};

export default FlashCards;
